'use client';

import { useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { hasPermission, type Permission } from '@/lib/auth/types';
import { useAuth } from '@/lib/useAuth';
import { useToast } from '@/components/ToastProvider';
import { CreateInvoiceDialog } from '@/components/billing/invoices/CreateInvoiceDialog';

const CREATE_ACTIONS = [
    { id: 'new-customer', title: 'New Customer', subtitle: 'Add a customer', permission: 'edit:crm', href: '/customers-hub?tab=directory&action=new' },
    { id: 'new-estimate', title: 'New Estimate', subtitle: 'Create estimate with cost-driven pricing', permission: 'edit:crm', href: '/estimates/unified' },
    { id: 'new-invoice', title: 'New Invoice', subtitle: 'Create an invoice for a property', permission: 'edit:financials', href: null },
    { id: 'log-request', title: 'Log a request', subtitle: 'Record a phone or walk-in enquiry', permission: 'edit:crm', href: '/growth?tab=pipeline&action=log-request' },
] as const satisfies ReadonlyArray<{ id: string; title: string; subtitle: string; permission: Permission; href: string | null }>;

export type CreateAction = typeof CREATE_ACTIONS[number];

/** Shared office creation choices; all invoice entry points reuse the existing form. */
export function useCreateActions() {
    const { user } = useAuth();
    const router = useRouter();
    const { toast } = useToast();
    const [invoiceOpen, setInvoiceOpen] = useState(false);
    const actions = useMemo(() => CREATE_ACTIONS.filter(action =>
        user && hasPermission(user.role, action.permission)
    ), [user]);

    function create(action: CreateAction) {
        if (!actions.some(allowed => allowed.id === action.id)) return;
        if (action.href) router.push(action.href);
        else setInvoiceOpen(true);
    }

    const invoiceDialog = invoiceOpen && actions.some(action => action.id === 'new-invoice') ? (
        <CreateInvoiceDialog
            onClose={() => setInvoiceOpen(false)}
            onCreated={() => {
                setInvoiceOpen(false);
                toast('Invoice created successfully');
            }}
        />
    ) : null;

    return { actions, create, invoiceDialog };
}
